import React, { useState, useEffect } from "react";
import useStyles from "./style";
import {
  Dialog,
  Typography,
  Button,
  IconButton,
  Grid,
  MenuItem,
  TextField,
  InputAdornment,
  Box,
} from "@mui/material";
import {
  CloseOutlined,
  EditOutlined,
  PersonAddAltOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import _ from "lodash";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import * as yup from "yup";

//Libs
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch ,useSelector} from "react-redux";

//Services
import HotelApi from "Services/Hotel";

//Components
import CountryCodeDropdown from "SharedComponents/CountryCodeDropdown";
import { getCountryCodes } from "Utils/country";

const HotelCreate = (props) => {
  const { openDialog, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const demoaccount_id = useSelector(
    (state) => state?.AuthReducer?.demoaccount_id || ""
  );

  const [passengerimage, setpassengerimage] = useState("/assets/images/user/avatar.png");
  const [countryCodeData, setCountryCodeData] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const maxFileSize = 5242880;

  const fileLengthValidator = (file) => {
    if (file.size > maxFileSize) {
      alert("File size is larger than 5MB");
      return { code: "file-too-large", message: "File too large" };
    }
    return null;
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
    accept: "image/*",
    validator: fileLengthValidator,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result;
          formik.setFieldValue("passengerProfileImage", file);
          setpassengerimage(base64String);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  useEffect(() => {
    try {
      const countries = getCountryCodes();
      setCountryCodeData(countries);
    } catch (error) {
      console.error("Error fetching country codes:", error);
    }
  }, []);

  const handleAddHotel = (formValues) => {
    dispatch(showLoader("Loading please wait..."));
    const data = {
      firstName: formValues?.firstName,
      country: formValues?.country,
      countryCode: formValues?.countryCode,
      mobileNumber: formValues?.mobile,
      email: formValues?.email,
      image: formValues?.passengerProfileImage,
      password: formValues?.password,
      hotelPercentage: formValues?.hotelPercentage,
      passConfirmation: formValues?.passConfirmation,
      address: formValues?.address,
      demoaccount_id: demoaccount_id || "",
    };

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "Hotel Created Successfully",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "success",
          })
        );
        formik.resetForm();
        setpassengerimage("/assets/images/user/avatar.png");
        handleClose();
      }
    };

    const onFailure = (err) => {
      let messages;
      const messagesArray = err?.response?.data?.data || [];
      if (messagesArray.length > 0) {
        messages = messagesArray.map((item) => item.msg).join(", ");
      } else {
        messages = err?.response?.data?.message;
      }
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: messages || "Error while creating Hotel",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };

    HotelApi.addHotel({ ...data }).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      email: "",
      password: "",
      passConfirmation: "",
      hotelPercentage: "",
      country: "",
      mobile: "",
      countryCode: "",
      address: "",
      passengerProfileImage: "",
    },
    validationSchema: yup.object({
      firstName: yup.string().required("Hotel Name is required").nullable(),
      email: yup.string().email("Invalid email format").required("Email is required").nullable(),
      countryCode: yup.string().required("Country Code is required").nullable(),
      mobile: yup
        .string()
        .matches(/^\d{1,15}$/, "Invalid mobile number")
        .required("Mobile is required")
        .nullable(),
      password: yup
        .string()
        .min(6, "Password must be at least 6 characters")
        .required("New password is required")
        .nullable(),
      passConfirmation: yup
        .string()
        .required("Confirm password is required")
        .oneOf([yup.ref("password"), null], "Passwords must match")
        .nullable(),
      hotelPercentage: yup
        .number()
        .typeError("Must be a number")
        .required("Hotel percentage is required")
        .min(0, "Cannot be less than 0")
        .max(100, "Cannot be more than 100")
        .nullable(),
      address: yup.string().required("Address is required").nullable(),
      country: yup.string().required("Country is required").nullable(),
      passengerProfileImage: yup.mixed().required("Hotel logo is required").nullable(),
    }),
    enableReinitialize: true,
    onSubmit: handleAddHotel,
  });

  return (
    <Dialog
      open={openDialog}
      onClose={() => {
        formik.resetForm();
        handleClose();
      }}
      classes={{ paper: classes.dialogPaper }}
      maxWidth={false}
    >
      {/* Header */}
      <Box className={classes.dialogHeader}>
        <Typography className={classes.dialogHeaderTitle}>Add Hotel / Restaurant</Typography>
        <IconButton className={classes.closeIconBtn} onClick={handleClose}>
          <CloseOutlined />
        </IconButton>
      </Box>

      {/* Body */}
      <Box className={classes.dialogBody}>
        {/* Left – Profile Photo */}
        <Box className={classes.leftColumn}>
          <Box className={classes.avatarWrapper}>
            <img
              src={passengerimage}
              alt="Profile"
              className={classes.avatarImg}
            />
            <Box
              component="span"
              className={classes.editIconOverlay}
              {...getRootProps()}
              onClick={open}
            >
              <input {...getInputProps()} />
              <EditOutlined />
            </Box>
          </Box>
          <Typography className={classes.photoLabel}>Hotel Logo</Typography>
          <Typography className={classes.photoSubLabel}>JPG, PNG up to 5MB</Typography>
          {formik.errors.passengerProfileImage && formik.touched.passengerProfileImage && (
            <Typography color="error" variant="caption" style={{ marginTop: 4 }}>
              {formik.errors.passengerProfileImage}
            </Typography>
          )}
        </Box>

        {/* Right – Form */}
        <Box className={classes.rightColumn}>
          <Grid container spacing={2}>
            {/* Hotel Name */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Hotel / Restaurant Name</Typography>
              <TextField
                id="firstName"
                name="firstName"
                placeholder="e.g. Grand Hotel"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>

            {/* Hotel Percentage */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Hotel Commission (%)</Typography>
              <TextField
                id="hotelPercentage"
                name="hotelPercentage"
                placeholder="e.g. 10"
                type="number"
                value={formik.values.hotelPercentage}
                onChange={formik.handleChange}
                error={formik.touched.hotelPercentage && Boolean(formik.errors.hotelPercentage)}
                helperText={formik.touched.hotelPercentage && formik.errors.hotelPercentage}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12}>
              <Typography className={classes.formLabel}>Email Address</Typography>
              <TextField
                id="email"
                name="email"
                placeholder="hotel@example.com"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>

            {/* Mobile Number */}
            <Grid item xs={12}>
              <Typography className={classes.formLabel}>Mobile Number</Typography>
              <Box className={classes.phoneRow}>
                <Box className={classes.countryCodeWrapper}>
                  <CountryCodeDropdown
                    formik={formik}
                    countryCodeData={countryCodeData}
                    name="countryCode"
                    id="countryCode"
                    label=""
                    error={formik.touched.countryCode && Boolean(formik.errors.countryCode)}
                    helperText={formik.touched.countryCode && formik.errors.countryCode}
                    roundedInputStyle={{}}
                  />
                </Box>
                <TextField
                  name="mobile"
                  id="mobile"
                  placeholder="000 000 0000"
                  value={formik.values.mobile}
                  onChange={formik.handleChange}
                  error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                  helperText={formik.touched.mobile && formik.errors.mobile}
                  fullWidth
                  size="small"
                  variant="outlined"
                  className={classes.formInput}
                />
              </Box>
            </Grid>

            {/* Password + Confirm Password */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Password</Typography>
              <TextField
                name="password"
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((p) => !p)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Confirm Password</Typography>
              <TextField
                name="passConfirmation"
                id="passConfirmation"
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="confirm-password"
                value={formik.values.passConfirmation}
                onChange={formik.handleChange}
                error={formik.touched.passConfirmation && Boolean(formik.errors.passConfirmation)}
                helperText={formik.touched.passConfirmation && formik.errors.passConfirmation}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword((p) => !p)}
                        edge="end"
                        size="small"
                      >
                        {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Country */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Country</Typography>
              <TextField
                name="country"
                id="country"
                select
                value={formik.values.country || ""}
                onChange={formik.handleChange}
                error={formik.touched.country && Boolean(formik.errors.country)}
                helperText={formik.touched.country && formik.errors.country}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (v) => v || <span style={{ color: "#9ca3af" }}>Select country</span>,
                }}
              >
                {countryCodeData.map((country) => (
                  <MenuItem key={country.code} value={country.name}>
                    {`${country.code} (${country.name})`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Address */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Address</Typography>
              <TextField
                name="address"
                id="address"
                placeholder="Enter address"
                value={formik.values.address}
                onChange={formik.handleChange}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Footer */}
      <Box className={classes.dialogFooter}>
        <Button
          className={classes.cancelButton}
          onClick={() => {
            formik.resetForm();
            handleClose();
          }}
        >
          Close
        </Button>
        <Button
          className={classes.submitButton}
          onClick={formik.handleSubmit}
          startIcon={<PersonAddAltOutlined />}
        >
          Create Hotel
        </Button>
      </Box>
    </Dialog>
  );
};

export default HotelCreate;
